import { AmplifyGet } from '@sunrun/sales-experience-shared/amplify-api-wrapper';
import { getEnvironment } from '@sunrun/sales-experience-shared';
import { useFeatureFlagString } from '@sunrun/sales-experience-shared/react-feature-flags';
import useSWR from 'swr';
import { FEATURE_FLAGS } from 'constants/FeatureFlags';
import { useHybridToken } from 'providers/Auth';
import { logger } from 'providers/logger';
import { useUserProfile } from 'providers/UserProfile';

type TrainingEnrollmentStatus =
  | 'enrolled'
  | 'in progress'
  | 'completed'
  | 'overdue';

type Training = {
  courseUniqueId: string;
  courseInternalId: number;
  courseName: string;
  enrollmentStatus: TrainingEnrollmentStatus;
  enrollmentStartDate: string;
  /** ISO date string — this is the due date */
  enrollmentEndDate: string;
  courseUrl: string;
};

type TrainingAlertLevel = 'red' | 'yellow' | 'info';

type TrainingAlert = {
  alertLevel: TrainingAlertLevel;
  courseUrl: string;
  enrollmentEndDate: string;
};

function getTrainingAlert(trainings: Training[]): TrainingAlert | undefined {
  const overdueTraining = trainings.find(
    (t) => t.enrollmentStatus === 'overdue',
  );

  if (overdueTraining) {
    return {
      alertLevel: 'red',
      courseUrl: overdueTraining.courseUrl,
      enrollmentEndDate: overdueTraining.enrollmentEndDate,
    };
  }

  const dueSoonTraining = trainings.find((t) => isTrainingDueSoon(t));

  if (dueSoonTraining) {
    return {
      alertLevel: 'yellow',
      courseUrl: dueSoonTraining.courseUrl,
      enrollmentEndDate: dueSoonTraining.enrollmentEndDate,
    };
  }

  const dueWithin14DaysTraining = trainings.find((t) =>
    isTrainingDueWithin14Days(t),
  );

  if (dueWithin14DaysTraining) {
    return {
      alertLevel: 'info',
      courseUrl: dueWithin14DaysTraining.courseUrl,
      enrollmentEndDate: dueWithin14DaysTraining.enrollmentEndDate,
    };
  }

  return undefined;
}

function isTrainingOverdue(training: Training): boolean {
  return training.enrollmentStatus === 'overdue';
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

function isTrainingStale(training: Training): boolean {
  // If the training has no due date, it is stale because we don't know when it's due
  if (!training.enrollmentEndDate) return true;

  return (
    Date.now() - new Date(training.enrollmentEndDate).getTime() > THIRTY_DAYS_MS
  );
}

function isTrainingDueSoon(training: Training): boolean {
  if (
    training.enrollmentStatus === 'completed' ||
    training.enrollmentStatus === 'overdue'
  )
    return false;
  const threeDaysMs = 3 * 24 * 60 * 60 * 1000;
  const timeLeft = new Date(training.enrollmentEndDate).getTime() - Date.now();
  return timeLeft > 0 && timeLeft <= threeDaysMs;
}

function isTrainingDueWithin14Days(training: Training): boolean {
  if (
    training.enrollmentStatus === 'completed' ||
    training.enrollmentStatus === 'overdue'
  )
    return false;
  const fourteenDaysMs = 14 * 24 * 60 * 60 * 1000;
  const timeLeft = new Date(training.enrollmentEndDate).getTime() - Date.now();
  return timeLeft > 0 && timeLeft <= fourteenDaysMs;
}

function filterVisibleTrainings(trainings: Training[]): Training[] {
  const now = new Date();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

  return trainings
    .filter((t) => {
      if (t.enrollmentStatus !== 'completed') return true;
      return (
        now.getTime() - new Date(t.enrollmentEndDate).getTime() <= sevenDaysMs
      );
    })
    .sort(
      (a, b) =>
        new Date(a.enrollmentEndDate).getTime() -
        new Date(b.enrollmentEndDate).getTime(),
    );
}

function parseStagingMockTrainings(flagValue: string): Training[] | undefined {
  try {
    const parsed: unknown = JSON.parse(flagValue);
    if (!Array.isArray(parsed)) {
      logger.warn(
        'Trainings mock feature flag value is not an array; falling back to API',
      );
      return undefined;
    }
    return parsed as Training[];
  } catch (error) {
    logger.warn(
      'Failed to parse trainings mock feature flag value; falling back to API',
    );
    return undefined;
  }
}

async function fetchTrainings(hybridToken: string): Promise<Training[]> {
  const data = await AmplifyGet<{ trainings: Training[] }>(
    'Get Trainings',
    'SalesExpDashboardApi',
    '/trainings',
    {
      headers: {
        Authorization: `Bearer ${hybridToken}`,
      },
    },
    logger,
  );
  return data.trainings ?? [];
}

const useTrainings = (): {
  trainings: Training[];
  trainingAlert: TrainingAlert | undefined;
  isTrainingsLoading: boolean;
  trainingsError: Error | undefined;
} => {
  const { userProfile } = useUserProfile();
  const { hybridToken } = useHybridToken();
  const mockTrainingsFlagValue = useFeatureFlagString(
    FEATURE_FLAGS.RUN_X_TRAININGS_MAJSTG_MOCKED_DATA,
  );
  const stagingMockTrainings =
    getEnvironment() === 'staging' && mockTrainingsFlagValue
      ? parseStagingMockTrainings(mockTrainingsFlagValue)
      : undefined;

  const shouldFetchTrainings =
    !stagingMockTrainings && hybridToken && userProfile;

  const { data, error, isLoading } = useSWR(
    shouldFetchTrainings ? ['/trainings', hybridToken] : null,
    ([, token]) => fetchTrainings(token as string),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  const rawData = stagingMockTrainings ?? data;

  if (!rawData) {
    return {
      trainings: [],
      trainingAlert: undefined,
      isTrainingsLoading: stagingMockTrainings ? false : isLoading,
      trainingsError: error,
    };
  }

  const freshTrainings = rawData.filter((t) => !isTrainingStale(t));
  const trainings = filterVisibleTrainings(freshTrainings);
  const trainingAlert = getTrainingAlert(freshTrainings);

  return {
    trainings,
    trainingAlert,
    isTrainingsLoading: stagingMockTrainings ? false : isLoading,
    trainingsError: error,
  };
};

export {
  fetchTrainings,
  filterVisibleTrainings,
  getTrainingAlert,
  isTrainingDueSoon,
  isTrainingDueWithin14Days,
  isTrainingOverdue,
  isTrainingStale,
  parseStagingMockTrainings,
  useTrainings,
  type Training,
  type TrainingAlert,
  type TrainingAlertLevel,
  type TrainingEnrollmentStatus,
};
